import React from "react";

// ── Единый календарь-фильтр периода (одна дата ИЛИ диапазон) ──────────────
// Кнопка 📅 в строке фильтров. Клик открывает ОДИН месячный календарь:
// первый клик — начало, второй — конец (диапазон подсвечивается); один клик
// + «Применить» (или два клика по одной дате) = один день. Отдаёт date_from/date_to
// (YYYY-MM-DD). При конфликте с год/месяц/день — период главнее (см. buildQueryFromFilters).
const PC_MONTHS_RU = ["январь","февраль","март","апрель","май","июнь","июль","август","сентябрь","октябрь","ноябрь","декабрь"];
const PC_WD = ["Пн","Вт","Ср","Чт","Пт","Сб","Вс"];

function pcFmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function pcParse(iso?: string): Date | null {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
function pcShort(iso: string): string {
  const d = pcParse(iso);
  if (!d) return iso;
  return `${String(d.getDate()).padStart(2, "0")} ${PC_MONTHS_RU[d.getMonth()].slice(0, 3)}`;
}

export function PeriodCalendar(props: {
  dateFrom?: string;
  dateTo?: string;
  onApply: (from: string, to: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [selFrom, setSelFrom] = React.useState<string | undefined>(props.dateFrom);
  const [selTo, setSelTo] = React.useState<string | undefined>(props.dateTo);
  const [view, setView] = React.useState<{ y: number; m: number }>(() => {
    const base = pcParse(props.dateFrom) ?? new Date();
    return { y: base.getFullYear(), m: base.getMonth() };
  });
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  React.useEffect(() => {
    if (!open) return;
    setSelFrom(props.dateFrom);
    setSelTo(props.dateTo);
    const base = pcParse(props.dateFrom) ?? new Date();
    setView({ y: base.getFullYear(), m: base.getMonth() });
  }, [open, props.dateFrom, props.dateTo]);

  const hasPeriod = Boolean(props.dateFrom && props.dateTo);
  const label = hasPeriod
    ? (props.dateFrom === props.dateTo
        ? pcShort(props.dateFrom as string)
        : `${pcShort(props.dateFrom as string)} – ${pcShort(props.dateTo as string)}`)
    : "период";

  const onDay = (iso: string) => {
    if (!selFrom || (selFrom && selTo)) {
      setSelFrom(iso);
      setSelTo(undefined);
      return;
    }
    if (iso < (selFrom as string)) {
      setSelTo(selFrom);
      setSelFrom(iso);
    } else {
      setSelTo(iso);
    }
  };
  const apply = () => {
    if (!selFrom) return;
    props.onApply(selFrom, selTo ?? selFrom);
    setOpen(false);
  };
  const clear = () => {
    setSelFrom(undefined);
    setSelTo(undefined);
    props.onClear();
    setOpen(false);
  };
  const shiftMonth = (delta: number) => {
    setView((v) => {
      const nm = v.m + delta;
      return { y: v.y + Math.floor(nm / 12), m: ((nm % 12) + 12) % 12 };
    });
  };

  // сетка месяца (недели с понедельника)
  const first = new Date(view.y, view.m, 1);
  const startOffset = (first.getDay() + 6) % 7; // Пн=0
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells: (string | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(pcFmt(new Date(view.y, view.m, d)));
  while (cells.length % 7 !== 0) cells.push(null);

  const inRange = (iso: string) => {
    const a = selFrom;
    const b = selTo ?? selFrom;
    if (!a) return false;
    const lo = a < (b as string) ? a : (b as string);
    const hi = a < (b as string) ? (b as string) : a;
    return iso >= lo && iso <= hi;
  };
  const isEnd = (iso: string) => iso === selFrom || iso === selTo;

  const btnStyle: React.CSSProperties = {
    height: 36,
    padding: "0 12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.3)",
    background: hasPeriod ? "#0a3d66" : "#083264",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 8,
    whiteSpace: "nowrap",
  };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button type="button" onClick={() => setOpen((s) => !s)} style={btnStyle} title="Период (дата или диапазон)">
        <span>📅</span>
        <span>{label}</span>
        <span style={{ opacity: 0.8 }}>{open ? "▲" : "▼"}</span>
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            zIndex: 200,
            width: 300,
            background: "#0e2b4b",
            border: "1px solid rgba(255,255,255,0.28)",
            borderRadius: 12,
            padding: 12,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            color: "#fff",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <button type="button" onClick={() => shiftMonth(-1)} style={pcNavStyle}>‹</button>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{PC_MONTHS_RU[view.m]} {view.y}</div>
            <button type="button" onClick={() => shiftMonth(1)} style={pcNavStyle}>›</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {PC_WD.map((w) => (
              <div key={w} style={{ textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.6)", padding: "2px 0" }}>{w}</div>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((iso, i) => {
              if (!iso) return <div key={i} />;
              const active = inRange(iso);
              const end = isEnd(iso);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onDay(iso)}
                  style={{
                    height: 32,
                    border: "none",
                    borderRadius: end ? 8 : 6,
                    cursor: "pointer",
                    fontSize: 13,
                    color: "#fff",
                    background: end ? "#48B1CC" : active ? "rgba(72,177,204,0.30)" : "transparent",
                    fontWeight: end ? 700 : 400,
                  }}
                >
                  {Number(iso.slice(8, 10))}
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
            <button type="button" onClick={clear} style={pcTextBtnStyle}>Сбросить</button>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
              {selFrom ? (selTo && selTo !== selFrom ? `${pcShort(selFrom)} – ${pcShort(selTo)}` : pcShort(selFrom)) : "выберите дату(ы)"}
            </div>
            <button type="button" onClick={apply} disabled={!selFrom} style={{ ...pcApplyStyle, opacity: selFrom ? 1 : 0.5 }}>Применить</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const pcNavStyle: React.CSSProperties = {
  width: 28, height: 28, borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)",
  background: "#083264", color: "#fff", cursor: "pointer", fontSize: 16, lineHeight: "1",
};
const pcTextBtnStyle: React.CSSProperties = {
  height: 30, padding: "0 8px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.25)",
  background: "transparent", color: "#fff", cursor: "pointer", fontSize: 12,
};
const pcApplyStyle: React.CSSProperties = {
  height: 30, padding: "0 12px", borderRadius: 8, border: "none",
  background: "#48B1CC", color: "#062033", cursor: "pointer", fontSize: 12, fontWeight: 700,
};
